<template>
  <div class="payment-modal">
    <div class="modal-body">
      <div class="modal-container">
        <img
          class="back"
          v-if="currentTab === 'stripe' && addingCard && !hideBack"
          src="@/assets/images/icons/arrow_left_dark.svg"
          alt="back"
          @click="toggleAddingCard"
        />
        <img
          v-if="!hideClose"
          class="close"
          src="@/assets/images/icons/close.svg"
          alt="close"
          @click="close"
        />
        <h2
          class="title"
          v-if="productType === availableProductTypes.COACH_SUBSCRIPTION"
        >
          Coach Subscription
        </h2>
        <SubscriptionDetails v-if="showDetails" />
        <PromoCodeForm
          v-if="productType === availableProductTypes.MEMBERSHIP_SUBSCRIPTION"
          :active-promo-codes="relevantAppliedPromoCodes"
          :show-promo-code-label="showPromoCodeLabel"
        />
        <div class="payment-tabs">
          <div class="payment-tab-container">
            <div class="tab-placeholder"></div>
            <div
              class="payment-tab stripe-tab"
              :class="{ 'selected-tab': currentTab === 'stripe' }"
              @click="changeTab('stripe')"
            >
              <img
                src="@/assets/images/payment/payment_methods.png"
                alt="card payment"
              />
            </div>
          </div>
          <div class="tab-gap"></div>
          <div class="payment-tab-container">
            <div
              class="payment-tab paypal-tab"
              :class="{ 'selected-tab': currentTab === 'paypal' }"
              @click="changeTab('paypal')"
            >
              <img src="@/assets/images/payment/paypal.png" alt="paypal" />
            </div>
            <div class="tab-placeholder"></div>
          </div>
        </div>
        <div v-if="loading">
          <lottie
            :options="{ animationData: LoadingAnimation }"
            :width="24"
            :height="24"
          />
        </div>
        <div v-else-if="currentTab === 'stripe'">
          <StripeTab
            @trackPaymentEvents="trackPaymentEvents"
            @toggleAddingCard="toggleAddingCard"
            :redirectPath="redirectPath"
            @extraAction="extraAction"
            :productType="productType"
            v-if="addingCard"
            :trial="trial"
            :btnCopy="btnCopy"
            :promo-codes="relevantAppliedPromoCodes"
          />
          <!-- <SavedCardTab
            @toggleAddingCard="toggleAddingCard"
            @extraAction="extraAction"
            @trackPaymentEvents="trackPaymentEvents"
            :redirectPath="redirectPath"
            :productType="productType"
            v-else
            :trial="trial"
            :btnCopy="btnCopy"
          /> -->
        </div>
        <PaypalTab
          v-else
          @trackPaymentEvents="trackPaymentEvents"
          :error="error"
          :productType="productType"
          @extraAction="extraAction"
          :paypalQueryString="paypalQueryString"
          :trial="trial"
          :promo-codes="relevantAppliedPromoCodes"
        />
        <div class="help" v-if="!hideHelp">
          <img src="@/assets/images/icons/chat/chat_grey.svg" alt="chat" />
          Need help?&nbsp;
          <span class="link" @click="startHelpChat"
            >Start Live Chat Support</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from "vuex"
import PromoCodeForm from "./PromoCodeForm"
import StripeTab from "./StripeTab"
import PaypalTab from "./PaypalTab"
// import SavedCardTab from "@/components/payment_v2/SavedCardTab.vue"
import SubscriptionDetails from "./SubscriptionDetails"
import LoadingAnimation from "@/assets/animations/ui/loading.json"
import Lottie from "vue-lottie"
import { PRODUCT_TYPE } from "@/constants/payment"
import { filterRelevantPromoCodes } from "@/store/payment/payment.utils"

export default {
  props: {
    productType: {
      type: String,
      default: PRODUCT_TYPE.POINT_PURCHASE,
    },
    btnCopy: {
      type: String,
      default: "PROCEED",
    },
    promoCodeShow: {
      type: Boolean,
      default: true,
    },
    maxNumPromoCodes: {
      type: Number,
      default: null,
    },
    hideHelp: {
      type: Boolean,
    },
    hideClose: {
      type: Boolean,
    },
    redirectPath: {
      type: String,
      default: "/dashboard",
    },
    showDetails: {
      type: Boolean,
      default: false,
    },
    initTab: {
      type: String,
    },
    hideBack: {
      type: Boolean,
    },
    paypalQueryString: {
      type: String,
    },
    trial: {
      type: Boolean,
      default: false,
    },
    showPromoCodeLabel: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    PromoCodeForm,
    StripeTab,
    PaypalTab,
    SubscriptionDetails,
    // SavedCardTab,
    Lottie,
  },
  data: function () {
    return {
      availableProductTypes: PRODUCT_TYPE,
      LoadingAnimation,
      currentTab: "stripe",
      error: null,
      addingCard: true,
      loading: true,
    }
  },
  computed: {
    ...mapGetters("Optimizely", ["optimizelyData"]),
    ...mapGetters("User", ["getUser"]),
    ...mapGetters("Payment", [
      "appliedPromoCodes",
    ]),
    ...mapGetters("PaymentLegacy", ["trialIneligibility"]),
    ...mapGetters("Store/Gift", ["currentGift"]),
    ...mapGetters("Store", [
      "subscribedItem",
      "selectedProduct",
      "subscriptionOptions",
      "savedPaymentMethods",
      "trialProductOffer",
    ]),
    price () {
      if (!this.selectedProduct) {
        return 96
      }
      return this.selectedProduct.billing_details.regular.price.amount
    },
    relevantAppliedPromoCodes () {
      const promoCodes = this.appliedPromoCodes.map(appliedPromoCode => appliedPromoCode.promo_code)
      const relevantPromoCodes = filterRelevantPromoCodes(promoCodes, [this.selectedProduct])
      return this.maxNumPromoCodes !== null ? relevantPromoCodes.slice(0, this.maxNumPromoCodes) : relevantPromoCodes
    },
  },
  methods: {
    ...mapActions("Util", ["startHelpChat"]),
    ...mapActions("Optimizely", ["trackEvent"]),
    ...mapActions("Payment", [
      "fetchAppliedPromoCodes",
    ]),
    ...mapActions("Store", ["getSubscriptionOptions", "loadUserStripeCards"]),
    ...mapMutations("Store", ["setSelectedProduct", "setSelectedSavedCard"]),
    ...mapMutations("PaymentLegacy", ["setTrialIneligibility"]),
    changeTab (tab) {
      this.currentTab = tab
    },
    close () {
      this.$emit("closePaymentModal", false)
    },
    async trackStartedCheckout () {
      try {
        let checkoutInfo
        if (
          [
            PRODUCT_TYPE.MEMBERSHIP_SUBSCRIPTION,
            PRODUCT_TYPE.MEMBERSHIP_TRIAL,
            PRODUCT_TYPE.COACH_SUBSCRIPTION,
          ].includes(this.productType)
        ) {
          const price = this.selectedProduct.billing_details.trial
            ? this.selectedProduct.billing_details.trial.price.amount
            : this.selectedProduct.billing_details.regular.price.amount
          checkoutInfo = {
            $value: price,
            AddedItemProductName: this.selectedProduct.name,
            AddedItemPrice: price,
            AddedItemQuantity: 1,
            ItemNames: [this.selectedProduct.name],
            CheckoutURL: window.location.href,
            Items: [
              {
                ProductName: this.selectedProduct.name,
                Price: price,
                Quantity: 1,
              },
            ],
          }
        } else if (this.productType === PRODUCT_TYPE.POINT_PURCHASE) {
          checkoutInfo = {
            $value: this.selectedProduct.price,
            AddedItemProductName: `${this.selectedProduct.amount} Points`,
            AddedItemPrice: this.selectedProduct.price,
            AddedItemQuantity: 1,
            ItemNames: [`${this.selectedProduct.amount} Points`],
            CheckoutURL: window.location.href,
            Items: [
              {
                ProductName: `${this.selectedProduct.amount} Points`,
                Price: this.selectedProduct.price,
                Quantity: 1,
              },
            ],
          }
        } else if (this.productType === PRODUCT_TYPE.GIFT_PURCHASE) {
          const price = this.currentGift.product.current_price
          const name = this.currentGift.product.name
          checkoutInfo = {
            $value: price,
            AddedItemProductName: name,
            AddedItemPrice: price,
            AddedItemQuantity: 1,
            ItemNames: [name],
            CheckoutURL: window.location.href,
            Items: [
              {
                ProductName: name,
                Price: price,
                Quantity: 1,
              },
            ],
          }
        } else {
          return
        }

        window._learnq.push(["track", "Started Checkout", checkoutInfo])
      } catch (error) {
        console.error(error)
      }
    },
    trackPaymentEvents (method) {
      try {
        if (
          [
            PRODUCT_TYPE.MEMBERSHIP_SUBSCRIPTION,
            PRODUCT_TYPE.MEMBERSHIP_TRIAL,
          ].includes(this.productType)
        ) {
          this.trackEvent("subscribe")
        }
      } catch (error) {}
    },
    checkPaypalError () {
      this.error = this.$route.query["error"]
      if (this.error) {
        this.changeTab("paypal")
      }
    },
    toggleAddingCard () {
      this.addingCard = !this.addingCard
    },
    extraAction () {
      this.$emit("extraAction")
    },
  },
  watch: {
    trialIneligibility (val) {
      if (val && this.productType === PRODUCT_TYPE.MEMBERSHIP_TRIAL) {
        console.log(this.subscriptionOptions[0])
        this.setSelectedProduct(this.subscriptionOptions[0].product)
      }
    },
  },
  async mounted () {
    this.checkPaypalError()
    this.fetchAppliedPromoCodes()
    // await this.loadUserStripeCards()
    // if (this.savedPaymentMethods.length < 1) {
    //   this.addingCard = true
    // }
    if (!this.selectedProduct) {
      await this.getSubscriptionOptions()
    }
    if (this.initTab === "paypal") {
      this.currentTab = "paypal"
    }
    if (this.productType === PRODUCT_TYPE.MEMBERSHIP_SUBSCRIPTION) {
      this.setSelectedProduct(this.subscriptionOptions[0].product)
    } else if (this.productType === PRODUCT_TYPE.MEMBERSHIP_TRIAL) {
      if (this.trialProductOffer && !this.trialIneligibility) {
        this.setSelectedProduct(this.trialProductOffer)
      } else {
        this.setSelectedProduct(this.subscriptionOptions[0].product)
        this.setTrialIneligibility(true)
      }
    }
    this.loading = false
    this.trackStartedCheckout()
    this.trackAnalyticsEvent("View Subscribe Modal")
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/style/_mixins.scss";
@import "@/assets/style/_variables.scss";
.payment-modal {
  position: relative;
  width: 100%;
  border-radius: 4px;
  overflow: auto;
  max-height: 100%;
  max-width: 50rem;
  font-family: $font-poppins;
  &::-webkit-scrollbar {
    display: none;
  }
  @include phone-down {
    margin-top: 0rem;
    max-width: 95%;
    max-height: 95%;
  }
  .modal-body {
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.1);

    .modal-container {
      padding-top: 2rem;
      background: white;
      border-radius: 4px 4px 0 0;
      .back {
        position: absolute;
        top: 2rem;
        left: 2rem;
        z-index: 21;
        opacity: 0.4;
        cursor: pointer;
        &.hide-back {
          display: none;
        }
      }
      .close {
        position: absolute;
        top: 2rem;
        right: 2rem;
        z-index: 21;
        opacity: 0.4;
        cursor: pointer;
      }
      .title {
        font-size: 1.6rem;
        font-weight: 600;
        text-align: center;
      }
      .description {
        font-size: 1.8rem;
        line-height: 2.8rem;
        display: flex;
        justify-content: space-between;
        padding: 0 4rem;
        margin-top: 1.2rem;
      }
      .payment-tabs {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        margin: 1.9rem 0 3rem;
        @include phone-small {
          margin: 1rem 0 1.5rem;
        }
        .tab-gap {
          width: 1rem;
          height: 1px;
          flex: none;
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        }
        .payment-tab-container {
          width: calc(50% - 0.5rem);
          display: flex;
          align-items: flex-end;
          .tab-placeholder {
            border-bottom: 1px solid rgba(0, 0, 0, 0.1);
            width: 4rem;
            flex: none;
          }
          .payment-tab {
            width: 100%;
            height: 4.9rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid rgba(0, 0, 0, 0.1);
            border-radius: 4px 4px 0 0;
            cursor: pointer;
            &.selected-tab {
              border-top: 3px solid #f42557;
              height: 5.1rem;
              border-bottom: none;
            }
          }
        }
      }
    }
    .help {
      background: #f0f0f0;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 4rem;
      margin-top: 2rem;
      color: #8a8a9a;
      font-size: 1.2rem;
      img {
        margin-right: 0.25rem;
      }
      .link {
        font-weight: 600;
        cursor: pointer;
        text-decoration: underline;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/payment_v2/PaymentModal.vue